import { useParams, Link, Navigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { ArrowLeft, Calendar, Clock, MapPin, Users, UtensilsCrossed, Sparkles, ArrowRight, Check, Heart, Flower2, TreePine, Egg, Coffee, Euro } from 'lucide-react'
import PageHero from '../components/PageHero'
import { brunchService, Brunch, hasMenuContent } from '../services/brunchService'
import { getEventImageUrl } from '../config/api'

// Icon Mapping
const iconMap: Record<string, any> = {
  Heart,
  Flower2,
  TreePine,
  Egg,
  Coffee
}

export default function BrunchDetail() {
  const { brunchId } = useParams<{ brunchId: string }>()
  const [brunch, setBrunch] = useState<Brunch | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const loadBrunch = async () => {
      if (brunchId) {
        const data = await brunchService.getBrunchBySlug(brunchId)
        setBrunch(data)
      }
      setIsLoading(false)
    }
    loadBrunch()
  }, [brunchId])

  if (isLoading) {
    return (
      <div className="relative min-h-screen bg-[#121212] flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-brand-600"></div>
      </div>
    )
  }

  // Wenn Brunch nicht gefunden wurde, zur Übersichtsseite weiterleiten
  if (!brunch) {
    return <Navigate to="/brunch" replace />
  }

  const Icon = iconMap[brunch.icon_name] || Coffee

  // Animation Variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  }

  const fadeInScale = {
    hidden: { opacity: 0, scale: 0.98 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  }

  const staggerContainer = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.08,
      }
    }
  }

  return (
    <div className="relative min-h-screen bg-[#121212]">
      {/* Globaler Hintergrund */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0d0d0d] via-[#121212] to-[#0a0a0a]"></div>
        <div className="absolute top-[10%] left-[5%] w-[700px] h-[700px] bg-brand-800/[0.04] rounded-full blur-[140px]"></div>
        <div className="absolute top-[50%] right-[10%] w-[600px] h-[600px] bg-rose-900/[0.03] rounded-full blur-[120px]"></div>
      </div>

      <div className="relative z-10 overflow-x-hidden">
        {/* Hero Section mit Icon */}
        <div className="relative">
          {/* Zurück Button - absolut über dem Hero positioniert */}
          <div className="absolute top-8 left-0 right-0 z-20 container mx-auto px-6 md:px-12 lg:px-24">
            <Link to="/brunch">
              <motion.button
                whileHover={{ x: -4 }}
                className="inline-flex items-center gap-2 text-gray-300 hover:text-white transition-colors duration-300 group backdrop-blur-sm bg-black/20 px-4 py-2 rounded-lg border border-white/10"
              >
                <ArrowLeft className="w-5 h-5 transition-transform group-hover:-translate-x-1" />
                <span className="font-sans text-sm">Zurück zur Übersicht</span>
              </motion.button>
            </Link>
          </div>

          <PageHero
            icon={Icon}
            title={brunch.title.split(' ')[0]}
            titleAccent={brunch.title.split(' ').slice(1).join(' ')}
            description={brunch.subtitle}
            backgroundImage="/assets/heros/hero_bilder.jpg"
          />
        </div>

        {/* Hauptinhalt */}
        <motion.section
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="relative py-16 md:py-24 bg-gradient-to-b from-gray-900 via-[#0f0f0f] to-black"
        >
          <div className="container mx-auto px-6 md:px-12 lg:px-24">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start max-w-7xl mx-auto">
              
              {/* Linke Spalte: Premium Event Card mit Flyer */}
              <motion.div variants={fadeInScale}>
                <div className="lg:sticky lg:top-32 space-y-6">
                  <div className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-3xl overflow-hidden border border-brand-700/30 shadow-2xl shadow-brand-900/40">
                    
                    {/* Animated Background Orbs */}
                    <div className="absolute top-0 right-0 w-[300px] h-[300px] bg-gradient-to-br from-brand-800/20 via-brand-900/10 to-transparent rounded-full blur-3xl"></div>
                    <div className="absolute bottom-0 left-0 w-[250px] h-[250px] bg-gradient-to-tr from-rose-900/15 via-brand-800/10 to-transparent rounded-full blur-3xl"></div>
                    
                    {/* Top accent line */}
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-brand-700/50 to-transparent"></div>
                    
                    <div className="relative p-10 lg:p-12">
                      
                      {/* Content */}
                      <div className="text-center space-y-8">
                        {/* Icon mit Glow */}
                        <motion.div
                          whileHover={{ scale: 1.05, rotate: 5 }}
                          transition={{ duration: 0.3 }}
                          className="relative inline-block"
                        >
                          <div className="absolute inset-0 bg-gradient-to-br from-brand-600 to-rose-600 rounded-full blur-2xl opacity-40"></div>
                          <div className="relative bg-gradient-to-br from-gray-800 to-gray-900 rounded-full p-10 border border-brand-700/30 shadow-2xl">
                            <Icon className="w-24 h-24 text-brand-400 drop-shadow-2xl" strokeWidth={1.5} />
                          </div>
                        </motion.div>
                        
                        <div className="h-1 w-28 mx-auto bg-gradient-to-r from-brand-700 to-rose-600 rounded-full"></div>
                        
                        <div>
                          <h3 className="font-serif text-5xl lg:text-6xl text-white mb-4 tracking-tight leading-tight">{brunch.title}</h3>
                          <p className="font-sans text-sm uppercase tracking-[0.2em] text-brand-300/80">{brunch.subtitle}</p>
                        </div>

                        {/* Event Info Cards */}
                        <div className="space-y-3 pt-4">
                          <div className="flex items-center gap-4 bg-white/5 backdrop-blur-sm rounded-2xl px-6 py-5 border border-white/10 hover:border-brand-700/30 transition-colors">
                            <Calendar className="w-7 h-7 text-brand-400 flex-shrink-0" />
                            <div className="text-left flex-1">
                              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Datum</p>
                              <p className="font-sans text-lg text-white font-medium">
                                {new Date(brunch.event_date).toLocaleDateString('de-DE', {
                                  day: '2-digit',
                                  month: 'long',
                                  year: 'numeric'
                                })}
                              </p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-4 bg-white/5 backdrop-blur-sm rounded-2xl px-6 py-5 border border-white/10 hover:border-brand-700/30 transition-colors">
                            <Clock className="w-7 h-7 text-brand-400 flex-shrink-0" />
                            <div className="text-left flex-1">
                              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Uhrzeit</p>
                              <p className="font-sans text-base text-white">{brunch.event_time}</p>
                            </div>
                          </div>
                          
                          {brunch.price && (
                            <div className="flex items-center gap-4 bg-gradient-to-br from-brand-800/20 to-brand-900/10 backdrop-blur-sm rounded-2xl px-6 py-5 border border-brand-700/30 hover:border-brand-600/40 transition-colors">
                              <Euro className="w-7 h-7 text-brand-400 flex-shrink-0" />
                              <div className="text-left flex-1">
                                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Preis</p>
                                <p className="font-sans text-lg text-brand-400 font-semibold">{brunch.price}</p>
                              </div>
                            </div>
                          )}
                          
                          <div className="flex items-center gap-4 bg-white/5 backdrop-blur-sm rounded-2xl px-6 py-5 border border-white/10 hover:border-brand-700/30 transition-colors">
                            <MapPin className="w-7 h-7 text-brand-400 flex-shrink-0" />
                            <div className="text-left flex-1">
                              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Ort</p>
                              <p className="font-sans text-base text-white font-medium">Landgut Hückels May</p>
                            </div>
                          </div>
                        </div>

                        {/* CTA Button */}
                        <div className="pt-6">
                          <Link to="/kontakt">
                            <motion.button
                              whileHover={{ scale: 1.03, y: -2 }}
                              whileTap={{ scale: 0.98 }}
                              className="w-full px-10 py-5 bg-gradient-to-r from-brand-700 via-brand-600 to-rose-600 hover:from-brand-600 hover:via-brand-500 hover:to-rose-500 text-white font-sans text-sm tracking-[0.15em] uppercase rounded-full shadow-2xl shadow-brand-800/50 hover:shadow-brand-700/70 transition-all duration-500 inline-flex items-center justify-center gap-3"
                            >
                              <span className="font-semibold">Jetzt reservieren</span>
                              <ArrowRight className="w-5 h-5" />
                            </motion.button>
                          </Link>
                        </div>
                      </div>
                    </div>

                    {/* Bottom accent line */}
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-brand-700/50 to-transparent"></div>
                  </div>

                  {/* Flyer Image - Kompakt unter dem Button */}
                  <motion.div
                    variants={fadeInScale}
                    className="relative overflow-hidden rounded-2xl border border-white/10 shadow-xl"
                  >
                    <img
                      src={getEventImageUrl(brunch.flyer_image_url) || '/assets/brunch/brunch_flyer_fallback.jpg'}
                      alt={brunch.title}
                      className="w-full h-auto object-cover"
                      loading="lazy"
                      onError={(e) => {
                        if (e.currentTarget.src !== '/assets/brunch/brunch_flyer_fallback.jpg') {
                          e.currentTarget.src = '/assets/brunch/brunch_flyer_fallback.jpg'
                        }
                      }}
                    />
                    {/* Subtle Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none" />
                  </motion.div>

                  {/* Für wen ist dieser Brunch? – unter dem Flyer links */}
                  <motion.div 
                    variants={fadeInScale}
                    className="p-8 lg:p-10 bg-gradient-to-br from-gray-900/60 to-gray-900/40 border border-brand-700/20 rounded-2xl shadow-xl"
                  >
                    <div className="flex items-start gap-5">
                      <div className="p-3 bg-brand-900/30 border border-brand-700/30 rounded-xl flex-shrink-0">
                        <Users className="w-8 h-8 text-brand-400" />
                      </div>
                      <div>
                        <h4 className="font-serif text-2xl text-white mb-4">Für wen ist dieser Brunch?</h4>
                        <p className="font-sans text-base md:text-lg text-gray-300 leading-relaxed">
                          Perfekt für Paare, Familien und Freunde, die gemeinsam besondere Momente erleben möchten. 
                          Reservierungen sind erforderlich und können telefonisch oder über unser Kontaktformular vorgenommen werden.
                        </p>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </motion.div>

              {/* Rechte Spalte: Details */}
              <motion.div variants={fadeInUp} className="space-y-12">
                
                {/* Beschreibung */}
                <div>
                  <motion.div variants={fadeInUp}>
                    <div className="inline-flex items-center gap-2 px-5 py-2 bg-brand-900/30 border border-brand-700/30 text-brand-300 text-xs tracking-[0.2em] uppercase font-semibold rounded-full mb-8">
                      <Sparkles className="w-4 h-4" />
                      <span>Über dieses Event</span>
                    </div>
                  </motion.div>

                  <motion.h2 
                    variants={fadeInUp}
                    className="font-serif text-4xl md:text-5xl font-light text-white mb-6 leading-tight tracking-tight"
                  >
                    Ein besonderer
                    <br />
                    <span className="bg-gradient-to-r from-brand-700 to-rose-500 bg-clip-text text-transparent font-normal">
                      Genussmoment
                    </span>
                  </motion.h2>

                  <motion.p 
                    variants={fadeInUp}
                    className="font-sans text-lg md:text-xl text-gray-300 leading-relaxed"
                  >
                    {brunch.long_description}
                  </motion.p>
                </div>

                {/* Highlights */}
                {brunch.features && brunch.features.length > 0 && (
                  <motion.div variants={fadeInUp}>
                    <h3 className="font-serif text-3xl md:text-4xl font-light text-white mb-8">
                      Was Sie erwartet
                    </h3>
                    <div className="grid gap-4">
                      {brunch.features.map((feature, idx) => (
                        <motion.div
                          key={idx}
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          transition={{ delay: idx * 0.1 }}
                          viewport={{ once: true, amount: 0 }}
                          className="group flex items-start gap-4 p-6 bg-gradient-to-br from-gray-900/50 to-gray-900/30 border border-white/10 hover:border-brand-700/30 rounded-2xl hover:bg-gradient-to-br hover:from-gray-900/70 hover:to-gray-800/50 transition-all duration-300"
                        >
                          <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-r from-brand-700 to-rose-600 flex items-center justify-center shadow-lg shadow-brand-800/30">
                            <Check className="w-4 h-4 text-white" strokeWidth={3} />
                          </div>
                          <span className="font-sans text-base md:text-lg text-gray-200 group-hover:text-white transition-colors leading-relaxed">{feature}</span>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}

                {/* Kulinarische Highlights – immer anzeigen */}
                <motion.div variants={fadeInUp}>
                  <div className="flex items-center gap-3 mb-8">
                    <div className="p-3 bg-brand-900/30 border border-brand-700/30 rounded-xl">
                      <UtensilsCrossed className="w-7 h-7 text-brand-400" />
                    </div>
                    <h3 className="font-serif text-3xl md:text-4xl font-light text-white">
                      Kulinarische Highlights
                    </h3>
                  </div>
                  {hasMenuContent(brunch.menu_highlights) ? (
                    <div className="space-y-6">
                      {[
                        { key: 'fruehstueck' as const, label: 'Frühstück' },
                        { key: 'vorspeise' as const, label: 'Vorspeise' },
                        { key: 'hauptspeise' as const, label: 'Hauptspeise' },
                        { key: 'dessert' as const, label: 'Dessert' },
                      ].map(({ key, label }) => {
                        const items = brunch.menu_highlights[key] || []
                        if (items.length === 0) return null
                        return (
                          <div key={key}>
                            <h4 className="font-serif text-xl text-brand-400 mb-3">{label}</h4>
                            <div className="grid md:grid-cols-2 gap-3">
                              {items.map((item, idx) => (
                                <motion.div
                                  key={idx}
                                  initial={{ opacity: 0, y: 10 }}
                                  whileInView={{ opacity: 1, y: 0 }}
                                  transition={{ delay: idx * 0.05 }}
                                  viewport={{ once: true, amount: 0 }}
                                  className="flex items-center gap-3 p-5 bg-gradient-to-br from-gray-900/50 to-gray-900/30 border border-white/10 hover:border-brand-700/20 rounded-xl hover:bg-gradient-to-br hover:from-gray-900/60 hover:to-gray-800/40 transition-all duration-300"
                                >
                                  <div className="flex-shrink-0 w-2 h-2 rounded-full bg-gradient-to-r from-brand-600 to-rose-600"></div>
                                  <span className="font-sans text-sm md:text-base text-gray-300">{item}</span>
                                </motion.div>
                              ))}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  ) : (
                    <p className="text-gray-400 font-sans text-base md:text-lg leading-relaxed italic">
                      Das genaue Buffet veröffentlichen wir hier ca. einen Monat vor Event.
                    </p>
                  )}
                </motion.div>

              </motion.div>
            </div>

            {/* Button und Kontakt – zentriert unter beiden Spalten */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
              className="flex flex-col items-center justify-center space-y-6 pt-16 max-w-7xl mx-auto"
            >
              <Link to="/kontakt" className="w-full max-w-md flex justify-center">
                <motion.button
                  whileHover={{ scale: 1.03, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full px-12 py-6 bg-gradient-to-r from-brand-700 via-brand-600 to-rose-600 hover:from-brand-600 hover:via-brand-500 hover:to-rose-500 text-white font-sans text-sm tracking-[0.15em] uppercase rounded-full shadow-2xl shadow-brand-800/50 hover:shadow-brand-700/70 transition-all duration-500 inline-flex items-center justify-center gap-4"
                >
                  <span className="font-semibold">Jetzt Tisch reservieren</span>
                  <ArrowRight className="w-6 h-6" />
                </motion.button>
              </Link>

              <div className="text-center pt-2 border-t border-white/5 w-full max-w-md">
                <p className="font-sans text-sm text-gray-500 mb-2 uppercase tracking-wider">Kontakt</p>
                <p className="font-sans text-base text-gray-400">
                  Telefonisch unter <a href="tel:+4915755537615" className="text-brand-400 hover:text-brand-300 transition-colors font-medium">01575 5537615</a>
                </p>
              </div>
            </motion.div>
          </div>
        </motion.section>

        {/* Andere Brunch Events */}
        <motion.section
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
          className="relative py-32 md:py-40 bg-gradient-to-b from-black via-[#0a0a0a] to-black overflow-hidden"
        >
          <div className="absolute inset-0">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-gradient-to-br from-brand-800/8 via-brand-900/4 to-transparent rounded-full blur-3xl"></div>
          </div>

          <div className="container mx-auto px-6 md:px-12 lg:px-24 relative z-10">
            <motion.div 
              variants={fadeInUp}
              className="text-center mb-16"
            >
              <h2 className="font-serif text-5xl md:text-6xl font-light text-white mb-6 leading-tight">
                Weitere
                <br />
                <span className="bg-gradient-to-r from-brand-700 to-rose-500 bg-clip-text text-transparent font-normal">
                  Brunch Events
                </span>
              </h2>
              <p className="font-sans text-xl text-gray-400 max-w-2xl mx-auto">
                Entdecken Sie auch unsere anderen saisonalen Brunch-Angebote
              </p>
            </motion.div>

            <div className="text-center">
              <Link to="/brunch">
                <motion.button
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  className="inline-flex items-center gap-4 px-12 py-5 bg-gradient-to-r from-brand-700 to-brand-600 hover:from-brand-600 hover:to-rose-600 text-white font-sans text-sm tracking-[0.15em] uppercase rounded-full shadow-2xl shadow-brand-800/40 hover:shadow-brand-700/60 transition-all duration-500"
                >
                  <span className="font-semibold">Alle Brunch Events</span>
                  <ArrowRight className="w-5 h-5" />
                </motion.button>
              </Link>
            </div>
          </div>
        </motion.section>
      </div>
    </div>
  )
}
